import ControllerBase from '../../../../global/js/ControllerBase';
import { fromEvent, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { fromFetch } from 'rxjs/fetch';
import { RegisterFormResponseModel } from './m-formstep-personal-data.model';
import { HttpActionResponse } from 'global/interfaces/http.interface';
import { ComponentEvents } from 'global/interfaces/component.interface';

type ComponentRefs = {
	formWrapper: 'formWrapper';
};
export default class ControllerFormstepPersonalData extends ControllerBase<ComponentRefs> {
	private readonly componentRefs: ComponentRefs = {
		formWrapper: 'formWrapper',
	};

	private componentEvents: ComponentEvents = {
		success: '',
		failure: '',
	};

	/** Subscription management */
	private _unsubscribeAll: Subject<any>;

	private formUrl: string;

	constructor(element: HTMLElement) {
		super(element);

		this.init();
		this.observeFormSubmissions();
	}

	private init() {
		this._unsubscribeAll = new Subject();
		window.addEventListener('unload', (e) => {
			this._unsubscribeAll.next();
			this._unsubscribeAll.complete();
		});

		this.formUrl = <string>this.$element.dataset.formurl;

		this.componentEvents.success = this.$element.dataset.eventNameSuccess || '';
		this.componentEvents.failure = this.$element.dataset.eventNameFailure || '';
	}

	/**
	 * Returns the form html element
	 * @returns HTMLFormElement
	 */
	private getForm(): HTMLFormElement | undefined {
		const forms = this.$element.querySelectorAll<'form'>('form');
		if (forms) {
			return forms[0];
		}
	}

	/**
	 * Listen for onKeyDown events on components with error
	 * @returns HTMLFormElement
	 */
	private removeErrorFromEditedFields() {
		const fieldsWithError = this.$element.getElementsByClassName('uk-form-danger');
		fieldsWithError &&
			[...fieldsWithError].forEach((field) => {
				fromEvent(field, 'keyup').subscribe(() => {
					if (field && field?.nextElementSibling) {
						field.classList.remove('uk-form-danger');
						field.nextElementSibling.classList.add('uk-hidden');
					}
				});
			});
	}

	/**
	 * Add errors when request response data is received
	 * @returns HTMLFormElement
	 */
	private addErrorsToFieldsNeedingReview(errors) {
		const fieldsWithError = Object.keys(errors);
		const fieldsRefs = [...fieldsWithError.map((name) => this.$element.querySelectorAll(`input[name="${name}"]`)[0])];

		fieldsRefs &&
			[...fieldsRefs].forEach((field, i) => {
				field.classList.add('uk-form-danger');
				if (field.nextElementSibling) {
					field.nextElementSibling.innerHTML = errors[fieldsWithError[i]];
					field.nextElementSibling.classList.remove('uk-hidden');
				}
			});
	}

	/**
	 * Watch for form submitted events
	 * @returns void
	 */
	private observeFormSubmissions(): void {
		const form = this.getForm();
		if (form) {
			fromEvent(form, 'submit')
				.pipe(takeUntil(this._unsubscribeAll))
				.subscribe((e) => {
					e && e.preventDefault();

					// send a form submission to the API
					this.submitForm()
						.pipe(takeUntil(this._unsubscribeAll))
						.subscribe(
							(response) => {
								if (response?.data?.success) {
									// emit eventNameSuccess
									this.componentEvents.success
										? this.dispatchCustomEvent<RegisterFormResponseModel>(this.componentEvents.success, response.data, true)
										: undefined;
								} else {
									const errors = response?.data?.error;

									// add errors to DOM
									this.addErrorsToFieldsNeedingReview(errors);

									// start observing inputs for error removal
									this.removeErrorFromEditedFields();

									// emit eventNameFailure
									this.componentEvents.failure
										? this.dispatchCustomEvent<false>(this.componentEvents.success, false, true)
										: undefined;
								}
							},
							() => {
								/** show generic error message */
								return console.error('Some error occurred while submitting the form');
							},
						);
				});
		}
	}

	/**
	 * Returns an observable of the API response
	 * @returns Observable
	 */
	private submitForm(): Observable<HttpActionResponse<RegisterFormResponseModel, never> | undefined> {
		const form = this.getForm();
		if (form) {
			const submitUrl = this.getFormSubmitUrl();

			return fromFetch<HttpActionResponse<RegisterFormResponseModel, never>>(submitUrl.toString(), {
				selector: (response) => response.json(),
				method: 'POST',
				body: this.getFormData(),
			}).pipe(
				map((res) => {
					if (res && res) {
						return res;
					}

					// @todo - might be a message from the server here...
					return of(undefined);
				}),
				catchError((e) => {
					// handle error case or non-20x response
					// console.log(e);
					return of(e);
				}),
			);
		}
		return of(undefined);
	}

	/**
	 * Determines the form's submit URL for XHR submissions
	 * @returns URL
	 */
	private getFormSubmitUrl(): URL {
		const form = this.getForm();

		// set the url, based on the data attrib, or the form action
		let url = this.formUrl;

		if (this.formUrl.length === 0) {
			url = form ? form.getAttribute('action') || '' : '';
		}

		return new URL(url);
	}

	/**
	 * Returns the form data
	 * @returns FormData
	 */
	private getFormData(): FormData {
		const form = this.getForm();
		return new FormData(form);
	}
}
